import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * The header on top of a page.
 *
 * The title sits on the page itself; the tabs and the filter row share one
 * bordered panel under it. They belong together - both are ways of narrowing
 * the same list - and as two loose strips they read as unrelated chrome.
 *
 * It carries the page margins itself, because on most screens it is a sibling
 * of the scrolling list rather than part of it. A screen that scrolls its
 * header along with its content passes `inset={false}` and pads the column
 * instead, so the margin is applied once either way.
 */
export function PageHead({
  title, crumb, actions, tabs, tools, inset = true
}: {
  title?: string;
  crumb?: string;
  actions?: ReactNode;
  tabs?: ReactNode;
  tools?: ReactNode;
  inset?: boolean;
}) {
  return (
    <header
      className={cn(
        'z-[5] flex flex-none flex-col gap-[var(--gap)]',
        inset && 'px-[var(--pad)] pt-[calc(var(--gap)*.8)]'
      )}
    >
      {title || actions ? (
        <div className="flex items-end gap-3">
          {title ? (
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-[30px] font-extrabold leading-[1.1] tracking-[-.035em]">
                {title}
              </h1>
              {crumb ? <p className="mt-1 truncate text-[13.5px] text-mute">{crumb}</p> : null}
            </div>
          ) : (
            <div className="flex-1" />
          )}
          {actions ? <div className="flex flex-none items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}

      {tabs || tools ? (
        <div className="pugo-panel flex flex-col gap-[calc(var(--gap)*.7)] rounded-[calc(16px*var(--radius-mul,1))] border border-line-soft bg-surface-1 p-[calc(var(--gap)*.7)]">
          {tabs}
          {tools}
        </div>
      ) : null}
    </header>
  );
}
